import { synchronizationAction } from 'actions';
import { synchronizationConstant } from 'actionTypes';
import { RootState } from 'helpers/store';
import { queryClient } from 'index';
import _ from 'lodash';
import { SynchronizationService } from 'services/synchronization/synchronization.service';
import { call, put, select, takeEvery } from 'typed-redux-saga';
import {
  FetchDocumentListRequestAction,
  FetchProjectListRequestAction,
} from 'types/actions/synchronization';
import { history } from 'utils/history';

const getProjects = function* (action: FetchProjectListRequestAction) {
  const connectionId = action.payload.connection_id;
  try {
    if (!connectionId) yield* put(synchronizationAction.fetchProjectListSuccess([]));
    yield* put(synchronizationAction.fetchProjectListLoading(true));
    const data = yield* call(() => {
      return SynchronizationService.fetchListProjects({
        connection_id: connectionId,
      });
    });
    if (Array.isArray(data)) {
      yield* put(synchronizationAction.fetchProjectListSuccess(data));
    } else {
      yield* put(synchronizationAction.fetchProjectListSuccess([]));
    }
  } catch (error) {
    console.log(error);
  }
};

const getCurrentFolderName = (folderPath: string) => {
  const parts = folderPath.split('/').filter(Boolean);
  return parts.length > 0 ? parts[parts.length - 1] : '';
};

const getDocumentsByProject = function* (action: FetchDocumentListRequestAction) {
  try {
    const { connection_id, site_url, folder_path } = yield* select(
      (state: RootState) => state.synchronization?.current
    );
    if (!connection_id || !site_url) return;

    yield* put(synchronizationAction.fetchDocumentListLoading(true));
    const data = yield* call(() => {
      return SynchronizationService.fetchDocumentsByProject({
        connection_id,
        site: site_url,
        folder: action.payload,
      });
    });

    if (_.isArray(data)) {
      yield* put(synchronizationAction.fetchDocumentListSuccess(data));
    } else {
      // Navigate back to parent folder
      let parentFolderPath = folder_path.slice(0, folder_path.lastIndexOf('/'));
      let parentFolderName = getCurrentFolderName(parentFolderPath);

      if (parentFolderPath === '') {
        parentFolderName = '';
        parentFolderPath = '';
      }

      yield* put(
        synchronizationAction.updateCurrentFolder({
          folder_name: parentFolderName,
          folder_path: parentFolderPath,
        })
      );
      yield* put(synchronizationAction.fetchDocumentListByProject(parentFolderPath));
    }
  } catch (error) {
    console.log(error);
    // Navigate back to parent folder on error
    const { folder_path } = yield* select((state: RootState) => state.synchronization?.current);

    let parentFolderPath = folder_path.slice(0, folder_path.lastIndexOf('/'));
    let parentFolderName = getCurrentFolderName(parentFolderPath);

    if (parentFolderPath === '') {
      parentFolderName = '';
      parentFolderPath = '';
    }

    yield* put(
      synchronizationAction.updateCurrentFolder({
        folder_name: parentFolderName,
        folder_path: parentFolderPath,
      })
    );
    yield* put(synchronizationAction.fetchDocumentListByProject(parentFolderPath));
  }
};

const synchDocumentRequest = function* () {
  try {
    const { folder_path, connection_id, site_url, selected_folders } = yield* select(
      (state: RootState) => state.synchronization.current
    );
    if (!connection_id || !site_url) return;

    const selectedFolderList = selected_folders?.length
      ? selected_folders.map((folder) => folder.display_name)
      : [];

    const data: any = yield* call(() => {
      return SynchronizationService.syncDocumentRequest(connection_id, {
        site_url,
        folder_path: folder_path,
        selected_folders: selectedFolderList,
      });
    });

    if (data.id) {
      history.push('/synchronization');
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: ['synchronizeHistory'],
        });
        put(synchronizationAction.syncDocumentProcessing(false));
      }, 2000);
    }

    if (!data.success) {
      yield* put(synchronizationAction.syncDocumentProcessing(false));
      put(synchronizationAction.updateSyncDocumentStatus(data));
    }
  } catch (error) {
    console.log(error);
  }
};

const getConnections = function* () {
  try {
    const data = yield* call(() => {
      // Only get confirmed connection
      return SynchronizationService.fetchListConnections({ confirmed: true });
    });
    yield* put(synchronizationAction.fetchConnectionListSuccess(data));
  } catch (error) {
    console.log(error);
  }
};

export const watchGetConnections = function* () {
  yield* takeEvery(synchronizationConstant.CONNECTIONS_FETCH_REQUESTED, getConnections);
};

export const watchGetProjects = function* () {
  yield* takeEvery(synchronizationConstant.PROJECTS_FETCH_REQUESTED, getProjects);
};

export const watchGetDocumentsByProject = function* () {
  yield* takeEvery(synchronizationConstant.DOCUMENT_FETCH_REQUESTED, getDocumentsByProject);
};

export const watchSynchDocumentRequest = function* () {
  yield* takeEvery(synchronizationConstant.SYNC_DOCUMENT_REQUESTED, synchDocumentRequest);
};
